"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";

interface LiteYouTubeProps {
  videoId: string;
  title?: string;
  className?: string;
}

/**
 * Lite YouTube Embed - loads YouTube iframe only on click
 * Dramatically improves initial page load by deferring heavy YouTube scripts
 */
export function LiteYouTube({ videoId, title = "YouTube video", className = "" }: LiteYouTubeProps) {
  const [isIframeLoaded, setIsIframeLoaded] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  const handleClick = () => {
    setIsIframeLoaded(true);
  };

  // Check if thumbnail exists using fetch to get proper HTTP status
  // YouTube returns 404 with a placeholder image, so we need to check the status code
  useEffect(() => {
    let cancelled = false;
    const maxResUrl = `https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`;
    const fallbackUrl = `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
    
    fetch(maxResUrl, { method: 'HEAD' })
      .then(response => {
        if (!cancelled) {
          // Set the appropriate URL based on whether maxresdefault exists
          setThumbnailUrl(response.ok ? maxResUrl : fallbackUrl);
        }
      })
      .catch(() => {
        // Fallback on network error
        if (!cancelled) {
          setThumbnailUrl(fallbackUrl);
        }
      });
    
    return () => {
      cancelled = true;
    };
  }, [videoId]);

  if (isIframeLoaded) {
    return (
      <div className={`relative aspect-video rounded-md overflow-hidden bg-black ${className}`}>
        <iframe
          width="100%"
          height="100%"
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1&controls=1&iv_load_policy=3&fs=1&playsinline=1&showinfo=0&cc_load_policy=0&disablekb=0&title=0&enablejsapi=1`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div 
      className={`relative aspect-video rounded-md overflow-hidden bg-black cursor-pointer group border-2 border-black ${className}`}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " " || e.key === "Spacebar" || e.key === "Space") {
          e.preventDefault();
          handleClick();
        }
      }}
      role="button"
      tabIndex={0}
      aria-label={`Play ${title}`}
    >
      {/* YouTube Thumbnail */}
      {thumbnailUrl && (
        <Image
          src={thumbnailUrl}
          alt={title}
          fill
          className="object-cover"
          loading="lazy"
          sizes="(max-width: 768px) 100vw, 896px"
          unoptimized
        />
      )}
      
      {/* Play Button Overlay */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="w-16 h-16 md:w-20 md:h-20 bg-red-600 rounded-full flex items-center justify-center">
          <svg 
            className="w-8 h-8 md:w-10 md:h-10 text-white ml-1" 
            fill="currentColor" 
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="M8 5v14l11-7z" />
          </svg>
        </div>
      </div>
      
      {/* Dark overlay for better button visibility */}
      <div className="absolute inset-0 bg-black/20" />
    </div>
  );
}

